import type { PostHog } from "posthog-js";

import type { PageView } from "./page-view";

/**
 * Product analytics for the admin's Pulpit and Growth pages. Without a yes to analytics on the
 * cookie banner PostHog runs cookieless: nothing is stored on the device and visitors are counted
 * by a daily-salted hash on PostHog's side, with no person profile. A yes switches to the normal
 * cookie mode, and identifying a signed-in person ties their first visit (referrer, UTM, country)
 * to our user id, which is what the admin's signup attribution reads.
 *
 * Every event carries `app`, so the admin can tell athlete-app traffic from coach-app traffic.
 * Page views are sent by hand once the router has loaded the page, so each one carries the route
 * pattern and the breadcrumb name the admin lists pages by.
 * Cookieless events are dropped unless "cookieless mode" is on in the PostHog project settings.
 *
 * The library is the largest thing every page would otherwise download first, and a page does not
 * need it to draw, so it is fetched when PostHog starts, once the visitor has answered the banner.
 */
const APP = "coach";
const DEFAULT_HOST = "https://eu.i.posthog.com";

type CaptureMode = "cookies" | "cookieless";

let loading: Promise<PostHog> | null = null;
let posthog: PostHog | null = null;
let mode: CaptureMode | null = null;
let currentPage: PageView | null = null;
let lastCapturedPathname: string | null = null;

const capturePage = (page: PageView) => {
  lastCapturedPathname = window.location.pathname;
  posthog?.capture("$pageview", { page_route: page.route, ...(page.name ? { page_name: page.name } : {}) });
};

/** Records the page the router just resolved; sent now, or once PostHog starts. A reload of the same path is not a new view. */
export const trackPageView = (page: PageView): void => {
  currentPage = page;
  if (mode === null || window.location.pathname === lastCapturedPathname) return;
  capturePage(page);
};

export const syncPosthogConsent = async (analyticsAllowed: boolean): Promise<void> => {
  // Unset in development and preview builds, so local clicks stay out of the numbers.
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key || typeof window === "undefined") return;

  // oxlint-disable-next-line rules/no-dynamic-import -- the point: every page draws without it (see above)
  loading ??= import("posthog-js").then((library) => {
    library.posthog.init(key, {
      api_host: import.meta.env.VITE_POSTHOG_HOST || DEFAULT_HOST,
      defaults: "2026-08-30",
      cookieless_mode: "on_reject",
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: true,
      before_send: (event) => (event ? { ...event, properties: { ...event.properties, app: APP } } : event),
    });
    return library.posthog;
  });
  posthog = await loading;
  const starting = mode === null;

  const next: CaptureMode = analyticsAllowed ? "cookies" : "cookieless";
  if (next === mode) return;
  mode = next;

  if (analyticsAllowed) posthog.opt_in_capturing();
  // With `on_reject` an opt-out keeps counting, cookieless, and clears the ids.
  else posthog.opt_out_capturing();

  // The router resolved the first page before the visitor answered the banner.
  if (starting && currentPage) capturePage(currentPage);
};

/** Ties the visit history to the account. A no-op while cookieless. */
export const identifyPosthogUser = (userId: string, properties: { email: string; name?: string }): void => {
  if (mode !== "cookies") return;
  posthog?.identify(userId, properties);
};

export const resetPosthogUser = (): void => {
  if (mode !== "cookies") return;
  posthog?.reset();
};
